// Vector charts for the PDF reports, drawn with @react-pdf/renderer <Svg>
// primitives (no raster images, no gradients). Two shapes only:
//   PowerCurve — 24-point hourly kW curve for day reports (line + area fill)
//   BarsChart  — per-day / per-month kWh bars for month & year reports

import { Svg, Path, Line, Rect, Text } from "@react-pdf/renderer";
import { COLORS, BODY_FONT } from "./theme";

const AXIS_LABEL = { fontSize: 6.5, fontFamily: BODY_FONT };
const MAX_LABEL = { fontSize: 7, fontFamily: BODY_FONT, fontWeight: 600 };

// Round a chart ceiling up to a "nice" 1 / 2 / 2.5 / 5 step so the axis reads clean.
function niceCeil(v) {
  if (v <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * pow;
}

function AxisText({ x, y, children, anchor = "middle" }) {
  return (
    <Text x={x} y={y} fill={COLORS.muted} textAnchor={anchor} style={AXIS_LABEL}>
      {children}
    </Text>
  );
}

// Hourly power curve (24 kW values) with a pale-gold area fill under the line.
export function PowerCurve({ curve, width = 499, height = 180 }) {
  const padL = 24;
  const padR = 8;
  const padT = 14;
  const padB = 18;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;
  const max = niceCeil(Math.max(...curve, 0.1));

  const x = (h) => padL + (h / 23) * plotW;
  const y = (v) => padT + (1 - v / max) * plotH;
  const base = y(0);

  const pts = curve.map((v, h) => [x(h), y(v)]);
  const line = pts.map(([px, py], i) => `${i === 0 ? "M" : "L"} ${px} ${py}`).join(" ");
  const area = `${line} L ${x(23)} ${base} L ${x(0)} ${base} Z`;

  return (
    <Svg width={width} height={height}>
      {/* horizontal gridlines + y labels */}
      {[0.5, 1].map((f) => (
        <Line
          key={f}
          x1={padL}
          y1={y(max * f)}
          x2={width - padR}
          y2={y(max * f)}
          stroke={COLORS.hairline}
          strokeWidth={0.6}
        />
      ))}
      <Text x={padL - 4} y={y(max) + 2.5} fill={COLORS.muted} textAnchor="end" style={MAX_LABEL}>
        {max}
      </Text>
      <Text x={padL - 4} y={y(max / 2) + 2.5} fill={COLORS.muted} textAnchor="end" style={AXIS_LABEL}>
        {max / 2}
      </Text>
      <AxisText x={padL - 4} y={base + 2.5} anchor="end">
        0
      </AxisText>

      {/* area + curve */}
      <Path d={area} fill={COLORS.paleGold} />
      <Path d={line} stroke={COLORS.deepGold} strokeWidth={1.4} fill="none" />

      {/* baseline + x ticks */}
      <Line x1={padL} y1={base} x2={width - padR} y2={base} stroke={COLORS.gold} strokeWidth={0.8} />
      {[0, 6, 12, 18, 23].map((h) => (
        <AxisText key={h} x={x(h)} y={height - 6}>
          {h === 23 ? "24" : String(h).padStart(2, "0")}
        </AxisText>
      ))}
    </Svg>
  );
}

// Vertical bars for month (per-day) and year (per-month) kWh series.
// bars: [{ label, value, highlight? }] — label may be "" to skip the tick.
export function BarsChart({ bars, width = 499, height = 150 }) {
  const padL = 28;
  const padR = 8;
  const padT = 14;
  const padB = 18;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;
  const max = niceCeil(Math.max(...bars.map((b) => b.value), 0.1));

  const slot = plotW / bars.length;
  const barW = Math.max(2, slot * 0.62);
  const base = padT + plotH;
  const y = (v) => padT + (1 - v / max) * plotH;

  return (
    <Svg width={width} height={height}>
      <Line x1={padL} y1={y(max)} x2={width - padR} y2={y(max)} stroke={COLORS.hairline} strokeWidth={0.6} />
      <Line x1={padL} y1={y(max / 2)} x2={width - padR} y2={y(max / 2)} stroke={COLORS.hairline} strokeWidth={0.6} />
      <Text x={padL - 4} y={y(max) + 2.5} fill={COLORS.muted} textAnchor="end" style={MAX_LABEL}>
        {max}
      </Text>
      <Text x={padL - 4} y={y(max / 2) + 2.5} fill={COLORS.muted} textAnchor="end" style={AXIS_LABEL}>
        {max / 2}
      </Text>
      <AxisText x={padL - 4} y={base + 2.5} anchor="end">
        0
      </AxisText>

      {bars.map((b, i) => {
        const bx = padL + i * slot + (slot - barW) / 2;
        const by = y(b.value);
        return (
          <Rect
            key={i}
            x={bx}
            y={by}
            width={barW}
            height={Math.max(1.2, base - by)}
            rx={Math.min(2, barW / 3)}
            fill={b.highlight ? COLORS.deepGold : COLORS.gold}
          />
        );
      })}

      <Line x1={padL} y1={base} x2={width - padR} y2={base} stroke={COLORS.gold} strokeWidth={0.8} />
      {bars.map((b, i) =>
        b.label ? (
          <AxisText key={i} x={padL + i * slot + slot / 2} y={height - 6}>
            {b.label}
          </AxisText>
        ) : null
      )}
    </Svg>
  );
}
